// Cliente del índice de completitud de HC (2026-09-10) — ver
// packages/web/src/api/lib/calidad-hc.ts / calidad-hc-muestreo.ts, expuestos
// como oRPC admin.calidadHcPaciente/admin.calidadHcMuestreo (no una ruta REST
// bajo /api/hce/*, a diferencia del resto de este directorio). Por eso acá sí
// se usa el cliente oficial de @orpc/client en vez de fetch a mano: el
// envoltorio RPC no es JSON plano y no vale la pena reimplementarlo. El tipo
// del router se declara a mano — mismo criterio de "duplicar entre paquetes"
// que CHECKLIST_COMITE/SISTEMA_SUBTIPO_CONSENTIMIENTO (packages/web no se
// puede importar desde este paquete, no comparten build).
import { createORPCClient } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import { vaultUrl } from './hceVault';
import type { ItemChecklistAuditoria } from './hceAuditoria';

export type IndiceCompletitud = {
  tenantSlug: string;
  patientId: string;
  generadoEn: string;
  /** Ítems que participan del score — ver notasEnfermeria abajo para lo que NUNCA participa. */
  items: ItemChecklistAuditoria[];
  /** Se muestra aparte; el backend lo suma a `score` solo cuando es evaluable (paciente con estancia IMP) — ver calidad-hc.ts. */
  notasEnfermeria: ItemChecklistAuditoria;
  score: { cumplidos: number; evaluables: number; porcentaje: number | null };
};

export type ResultadoMuestreo = {
  tenantSlug: string;
  generadoEn: string;
  tamanoSolicitado: number;
  pacientesElegibles: number;
  pacientesEvaluados: number;
  resultados: IndiceCompletitud[];
  indiceConsultorio: { cumplidos: number; evaluables: number; porcentaje: number | null };
};

// Alertas de completitud EN VIVO (2026-09-10) — ver
// packages/web/src/api/lib/calidad-hc-vivo.ts, expuesto como oRPC
// admin.alertasCompletitudVivas. A diferencia de IndiceCompletitud/
// ResultadoMuestreo (retrospectivo, por muestreo), esto agrega TODOS los
// Encounters de los últimos N días con nombre real de paciente y del último
// profesional que lo atendió — mismo tipo `ClaveAlertaCompletitud` duplicado
// a mano (mismo criterio de sincronización manual entre paquetes).
export type ClaveAlertaCompletitud = 'anamnesis-ingreso' | 'notas-evolucion' | 'epicrisis-alta';

export type AlertaViva = {
  encounterRef: string;
  patientId: string;
  patientName: string;
  practitionerName: string | null;
  clave: ClaveAlertaCompletitud;
  fecha: string;
};

type AdminCalidadHcClient = {
  admin: {
    calidadHcPaciente: (input: { token: string; slug: string; patientId: string }) => Promise<IndiceCompletitud>;
    calidadHcMuestreo: (input: { token: string; slug: string; tamano?: number }) => Promise<ResultadoMuestreo>;
    alertasCompletitudVivas: (input: { token: string; slug: string; dias?: number }) => Promise<AlertaViva[]>;
  };
};

const link = new RPCLink({ url: vaultUrl('/api/rpc') });
const client: AdminCalidadHcClient = createORPCClient(link);

export async function obtenerCalidadHcMuestreo(token: string, slug: string, tamano?: number): Promise<ResultadoMuestreo> {
  return client.admin.calidadHcMuestreo({ token, slug, tamano });
}

export async function obtenerCalidadHcPaciente(token: string, slug: string, patientId: string): Promise<IndiceCompletitud> {
  return client.admin.calidadHcPaciente({ token, slug, patientId });
}

export async function obtenerAlertasCompletitudVivas(token: string, slug: string, dias?: number): Promise<AlertaViva[]> {
  return client.admin.alertasCompletitudVivas({ token, slug, dias });
}
